/* eslint-disable */

/*
 * mpetroff/print-maps
 * https://github.com/mpetroff/print-maps
 *
 * I used the source code from the above repository. Thanks so much!
 *
 * -----LICENSE------
 * Print Maps - High-resolution maps in the browser, for printing
 * Copyright (c) 2015-2020 Matthew Petroff
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
 * THE SOFTWARE.
 */

import { jsPDF } from 'jspdf';
import { saveAs } from 'file-saver';
import mapboxgl, { accessToken, Map as MapboxMap } from 'mapbox-gl';
import 'js-loading-overlay';
import { fabric } from 'fabric';
import { loadingShow, loadingHide } from "@/utils";
import layer from '@/utils/constants/map/layer';
import ExportService from "@/components/services/ExportService";
import PrintableAreaManager from './printable-area-manager';

export const Format = {
  JPEG: 'jpg',
  PNG: 'png',
  PDF: 'pdf',
  SVG: 'svg',
} as const;
type Format = typeof Format[keyof typeof Format];

export const Unit = {
  // don't use inch unit. because page size setting is using mm unit.
  in: 'in',
  mm: 'mm',
} as const;
type Unit = typeof Unit[keyof typeof Unit];

export const Size = {
  A0: [1189, 841],
  A1: [841, 594],
  A2: [594, 420],
  A3: [420, 297],
  A4: [297, 210],
  A5: [210, 148],
  A6: [148, 105]

} as const;
type Size = typeof Size[keyof typeof Size];

export const PageOrientation = {
  Landscape: 'landscape',
  Portrait: 'portrait',
} as const;
type PageOrientation = typeof PageOrientation[keyof typeof PageOrientation];

export const DPI = {
  72: 72,
  96: 96,
  200: 200,
  300: 300,
  400: 400,
} as const;
type DPI = typeof DPI[keyof typeof DPI];

export default class MapGenerator {
  private map: MapboxMap;
  private width: number;
  private height: number;
  private dpi: number;
  private format: string;
  private unit: Unit;
  private accesstoken: string | undefined;
  private exportService: ExportService;
  private pageFormat: string;
  private printableArea: PrintableAreaManager | undefined;

  /**
   * Constructor
   * @param map MapboxMap object
   * @param size layout size. default is A4
   * @param dpi dpi value. deafult is 200
   * @param format image format. default is PNG
   * @param unit length unit. default is mm
   */
  constructor(
    map: MapboxMap,
    size: Size = Size.A4,
    dpi: number = 200,
    format: string = Format.PDF.toString(),
    unit: Unit = Unit.mm,
    accesstoken?: string,
  ) {
    this.map = map;
    this.width = size[0];
    this.height = size[1];
    this.dpi = dpi;
    this.format = format;
    this.unit = unit;
    this.accesstoken = accesstoken;
    this.exportService = new ExportService();
    this.pageFormat = Object.keys(Size)
      .find(key => Size[key].includes(this.width) && Size[key].includes(this.height))
      ?.toLocaleLowerCase()!;
  }

  /**
   * Generate and download Map image
   */
  generate() {
    const this_ = this;

    loadingShow();
    this.setSelectionLayers(this.map, "visible");

    // Calculate pixel ratio
    const actualPixelRatio: number = window.devicePixelRatio;
    Object.defineProperty(window, 'devicePixelRatio', {
      get() { return this_.dpi / 96; },
    });
    // Create map container
    const hidden = document.createElement('div');
    hidden.className = 'hidden-map';
    document.body.appendChild(hidden);
    const container = document.createElement('div');
    container.style.width = this.toPixels(this.width);
    container.style.height = this.toPixels(this.height);
    hidden.appendChild(container);

    const style = this.map.getStyle();
    if (style && style.sources) {
      const sources = style.sources;
      Object.keys(sources).forEach((name) => {
        const src = sources[name];
        Object.keys(src).forEach((key) => {
          // delete properties if value is undefined.
          // for instance, raster-dem might has undefined value in "url" and "bounds"
          if (!src[key]) delete src[key];
        });
      });
    }

    // Render map
    const renderMap = new MapboxMap({
      accessToken: this.accesstoken || accessToken,
      container,
      style,
      center: this.map.getCenter(),
      zoom: this.map.getZoom(),
      bearing: this.map.getBearing(),
      pitch: this.map.getPitch(),
      interactive: false,
      preserveDrawingBuffer: true,
      fadeDuration: 0,
      attributionControl: false,
      // hack to read transfrom request callback function
      transformRequest: (this.map as any)._requestManager._transformRequestFn,
    });

    // @ts-ignore
    const images = (this.map.style.imageManager || {}).images || [];
    Object.keys(images).forEach((key) => {
      renderMap.addImage(key, images[key].data);
    });

    renderMap.once('idle', () => {
      this.exportService.drawFrame(renderMap.getCanvas())
        .then(canvas => {
          const fileName = `export-map.${this_.format}`;

          this.export(this_.format, canvas, fileName, renderMap)

          renderMap.remove();
          hidden.parentNode?.removeChild(hidden);
          Object.defineProperty(window, 'devicePixelRatio', {
            get() { return actualPixelRatio; },
          });

          this.setSelectionLayers(this.map, "none");
          loadingHide();
        });
    });
  }

  private export(format, canvas, fileName, renderMap) {
    switch (format) {
      case Format.PNG:
        this.toPNG(canvas, fileName);
        break;
      case Format.JPEG:
        this.toJPEG(canvas, fileName);
        break;
      case Format.PDF:
        this.toPDF(canvas, renderMap, fileName);
        break;
      case Format.SVG:
        this.toSVG(canvas, fileName);
        break;
      default:
        console.error(`Invalid file format: ${format}`);
        break;
    }
  }

  /**
   * Convert canvas to PNG
   * @param canvas Canvas element
   * @param fileName file name
   */
  private toPNG(canvas: HTMLCanvasElement, fileName: string) {
    canvas.toBlob((blob) => {
      // @ts-ignore
      saveAs(blob, fileName);
    });
  }

  /**
   * Convert canvas to JPEG
   * @param canvas Canvas element
   * @param fileName file name
   */
  private toJPEG(canvas: HTMLCanvasElement, fileName: string) {
    const uri = canvas.toDataURL('image/jpeg', 0.85);
    const a = document.createElement('a');
    a.href = uri;
    a.download = fileName;
    a.click();
    a.remove();
  }

  /**
   * Convert Map object to PDF
   * @param map mapboxgl.Map object
   * @param fileName file name
   */
  private toPDF(canvas: HTMLCanvasElement, map: mapboxgl.Map, fileName: string) {
    const pdf = new jsPDF({
      orientation: this.width > this.height ? 'l' : 'p',
      unit: this.unit,
      compress: true,
      format: this.pageFormat
    });

    pdf.addImage(canvas.toDataURL('image/png'), 'png', 0, 0, this.width, this.height, undefined, 'FAST');

    const { lng, lat } = map.getCenter();
    pdf.setProperties({
      title: map.getStyle().name,
      subject: `center: [${lng}, ${lat}], zoom: ${map.getZoom()}`,
      creator: 'Mapbox GL Export Plugin',
      author: '(c)Mapbox, (c)OpenStreetMap',
    });

    pdf.save(fileName);
  }

  /**
   * Convert canvas to SVG
   * this SVG export is using fabric.js. It is under experiment.
   * Please also see their document.
   * http://fabricjs.com/docs/
   * @param canvas Canvas element
   * @param fileName file name
   */
  private toSVG(canvas: HTMLCanvasElement, fileName: string) {
    const uri = canvas.toDataURL('image/png');
    fabric.Image.fromURL(uri, (image) => {
      const tmpCanvas = new fabric.Canvas('canvas');
      const pxWidth = Number(this.toPixels(this.width, this.dpi).replace('px', ''));
      const pxHeight = Number(this.toPixels(this.height, this.dpi).replace('px', ''));
      image.scaleToWidth(pxWidth);
      image.scaleToHeight(pxHeight);

      tmpCanvas.add(image);
      const svg = tmpCanvas.toSVG({
        // @ts-ignore
        x: 0,
        y: 0,
        width: pxWidth,
        height: pxHeight,
        viewBox: {
          x: 0,
          y: 0,
          width: pxWidth,
          height: pxHeight,
        },
      });
      const a = document.createElement('a');
      a.href = `data:application/xml,${encodeURIComponent(svg)}`;
      a.download = fileName;
      a.click();
      a.remove();
    });
  }

  /**
   * Convert mm/inch to pixel
   * @param length mm/inch length
   * @param conversionFactor DPI value. default is 96.
   */
  private toPixels(length: number, conversionFactor = 96) {
    if (this.unit === Unit.mm) {
      conversionFactor /= 25.4;
    }
    return `${conversionFactor * length}px`;
  }

  private setSelectionLayers(map, state) {
    map.setLayoutProperty(layer.SELECT_WATERLINE, "visibility", state);
    map.setLayoutProperty(layer.SELECT_WATERPOINT, "visibility", state);
    map.setLayoutProperty(layer.SELECT_AREAL_WATER, "visibility", state);
    map.setLayoutProperty(layer.SELECT_WATERMETERS_PUBLIC, "visibility", state);
    map.setLayoutProperty(layer.SELECT_WATERMETERS_RESIDENTIAL, "visibility", state);
    map.setLayoutProperty(layer.SELECT_ZONES_POLY, "visibility", state);
    map.setLayoutProperty(layer.SELECT_ZONE_POLY_BREAKDOWN, "visibility", state);
    map.setLayoutProperty(layer.SELECT_ZONES_LINE, "visibility", state);
    map.setLayoutProperty(layer.SELECT_ZONES_POINT, "visibility", state);
  }
}
